import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { ProjectReleaseFeed, PublicProjectRelease } from './public-release-feed.model';

/** Public contract major we understand. Unknown majors → fail soft (empty feed). */
const SUPPORTED_SCHEMA_MAJOR = 1;

/**
 * Reads the PUBLIC project-release feed straight from the GCS bucket
 * (`gs://i3deploy-public`), unauthenticated. The URL lives on
 * `storage.googleapis.com`, NOT `environment.backendDomain`, so the auth /
 * org-scope interceptors deliberately skip it — a plain public GET that works
 * even while the scale-to-zero i3deploy backend is asleep.
 *
 * Fails soft: any transport error, or a `schema_version` major we don't support,
 * resolves to an empty release list rather than throwing — the "what's new" UI
 * simply hides instead of crashing. See PUBLIC_FEED.md for the contract.
 */
@Injectable({ providedIn: 'root' })
export class PublicReleaseFeedService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.publicFeedBase;

  /** Public URL of a project's feed — safe to share / open in a browser. */
  feedUrl(orgSlug: string, projectSlug: string): string {
    return `${this.base}/${orgSlug}/projects/${projectSlug}/releases.json`;
  }

  /** Whole feed document, or `null` on error / unsupported schema. */
  getFeed(orgSlug: string, projectSlug: string): Observable<ProjectReleaseFeed | null> {
    return this.http.get<ProjectReleaseFeed>(this.feedUrl(orgSlug, projectSlug)).pipe(
      map((feed) => (this.isSupported(feed) ? feed : null)),
      catchError(() => of(null))
    );
  }

  /** Release list, newest first (empty on error / no published release). */
  getReleases(orgSlug: string, projectSlug: string): Observable<PublicProjectRelease[]> {
    return this.getFeed(orgSlug, projectSlug).pipe(map((feed) => feed?.releases ?? []));
  }

  /** Most recent published release, or `null` when the feed is empty. */
  getLatest(orgSlug: string, projectSlug: string): Observable<PublicProjectRelease | null> {
    return this.getReleases(orgSlug, projectSlug).pipe(map((rs) => rs[0] ?? null));
  }

  private isSupported(feed: ProjectReleaseFeed | null): boolean {
    const major = parseInt((feed?.schema_version ?? '').split('.')[0], 10);
    return major === SUPPORTED_SCHEMA_MAJOR;
  }
}
